import { Injectable } from '@angular/core';

import { Vault } from './vault';

const PREFIX = 'ninety.vault.';

/**
 * Vault implementation for the browser build.
 *
 * There is no Keychain in a browser, so the Durable Root sits in `localStorage`. This is a
 * knowingly weaker position than native and is accepted because the browser build of this app
 * is internal-only — see ADR 0003.
 *
 * Biometric sign-in reports itself permanently unavailable here for the same reason — see
 * `WebBiometricSignIn`.
 * Nothing about this class had to change to say so.
 */
@Injectable()
export class WebVault implements Vault {
  async read(key: string): Promise<string | null> {
    return localStorage.getItem(PREFIX + key);
  }

  async write(key: string, value: string): Promise<void> {
    localStorage.setItem(PREFIX + key, value);
  }

  async remove(key: string): Promise<void> {
    localStorage.removeItem(PREFIX + key);
  }

  async keys(): Promise<readonly string[]> {
    return Object.keys(localStorage)
      .filter(key => key.startsWith(PREFIX))
      .map(key => key.slice(PREFIX.length));
  }

  /**
   * The prefix is the namespace, so this removes exactly what {@link keys} would have listed.
   *
   * Nothing is caught. `localStorage` rarely fails a removal, and a failure here is the one the
   * caller has to hear about — see {@link Vault.purge}.
   */
  async purge(): Promise<void> {
    for (const key of Object.keys(localStorage).filter(key => key.startsWith(PREFIX))) {
      localStorage.removeItem(key);
    }
  }
}
